import styled from 'styled-components';

import media from '../../theme/media';

const shouldForwardInputContainerProp = (prop) => !['isLinedInput', 'isCalculatorInput', 'isReadOnly', 'isFocused', 'isError', 'isFilled'].includes(prop);
const shouldForwardInputLabelProp = (prop) =>
    !['isLinedInput', 'isCalculatorInput', 'isPseudoSelect', 'isSmsInput', 'isCenteredInput', 'isError', 'isReadOnly'].includes(prop);
const shouldForwardFloatingLabelProp = (prop) =>
    !['isLinedInput', 'isFilled', 'isFocused', 'isCaptcha', 'isCenteredInput', 'isReadOnly', 'isError'].includes(prop);
const shouldForwardHintContainerProp = (prop) => !['isLinedInput', 'hasComment', 'alwaysHasHint'].includes(prop);
const shouldForwardInputRightElementProp = (prop) => !['isInfoButtonForPseudoselect', 'isInfoButton'].includes(prop);

// TODO: no black (emphasised) border token for the focused state — using --fill_primary_default, like the primary button.
const focusedBorderColor = 'var(--fill_primary_default)';

const getLabelColor = (props) => {
    if (props.isReadOnly) {
        return 'var(--text_primary_main_disabled)';
    } else if (props.isError) {
        return 'var(--text_error_default)';
    } else if (props.isFocused) {
        return 'var(--text_primary_main_default)';
    } else {
        return 'var(--text_secondary_main_default)';
    }
};
const getBorderColor = (props) => {
    if (props.isError) {
        return 'var(--border_error_default)';
    } else if (props.isFocused) {
        return focusedBorderColor;
    } else {
        // TODO: lined inputs had a darker grey_40_lc underline; no border token of that weight — confirm --border_primary_main_default.
        return 'var(--border_primary_main_default)';
    }
};
const getInputColor = (props) => {
    if (props.isCalculatorInput && props.isError) {
        return 'var(--text_error_default)';
    }
    return props.isReadOnly ? 'var(--text_primary_main_disabled)' : 'var(--text_primary_main_default)';
};
const getInputHoverColor = (props) => {
    if (!props.isCalculatorInput) {
        return null;
    }
    return props.isError ? 'var(--text_error_default)' : 'var(--text_primary_main_hovered)';
};

const getFloatingLabelPosition = (props) => {
    if (props.isLinedInput) {
        return props.isFilled || props.isFocused ? '-4px' : '24px';
    } else {
        return props.isFilled || props.isFocused ? '-12px' : '16px';
    }
};
const getFloatingLabelDesktopPosition = (props) => {
    if (props.isLinedInput) {
        return props.isFilled || props.isFocused ? '-8px' : '28px';
    } else {
        return props.isFilled || props.isFocused ? '-14px' : '20px';
    }
};
const getFloatingLabelLeftPosition = (props) => {
    if (props.isLinedInput) {
        return '0';
    } else if (props.isCaptcha) {
        return 'calc(50% - 8px)';
    } else {
        return '8px';
    }
};
const getFloatingLabelLeftDesktopPosition = (props) => {
    if (props.isLinedInput) {
        return '0';
    } else if (props.isCaptcha) {
        return 'calc(50% - 8px)';
    } else {
        return '16px';
    }
};

export const InputContainer = styled.div.withConfig({ shouldForwardProp: shouldForwardInputContainerProp })`
    font-family: var(--font-main), sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    position: relative;
    border: ${(props) =>
        props.isLinedInput || props.isCalculatorInput
            ? null
            : `1px ${props.isReadOnly ? 'dashed var(--border_primary_main_disabled)' : 'solid var(--border_primary_main_default)'}`};
    border-bottom: ${(props) => (props.isCalculatorInput ? null : `1px ${props.isReadOnly ? `dashed` : `solid`} ${getBorderColor(props)}`)};
    border-radius: ${(props) => (props.isLinedInput ? null : '6px')};
    pointer-events: ${(props) => (props.isReadOnly ? 'none' : null)};

    &:after {
        display: ${(props) => (props.isCalculatorInput ? 'none' : null)};
        content: '';
        position: absolute;
        width: ${(props) => (props.isLinedInput ? '100%' : 'calc(100% - 4px)')};
        height: 1px;
        background: ${(props) => (!props.isFocused && !props.isError ? null : getBorderColor(props))};
        bottom: 0;
        left: ${(props) => (props.isLinedInput ? '0' : '2px')};

        ${media.desktop1920} {
            height: 2px;
        }
    }
`;

export const InputLabel = styled.label.withConfig({ shouldForwardProp: shouldForwardInputLabelProp })`
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    padding: ${(props) => (props.isLinedInput ? '0 0 6px 0' : props.isCalculatorInput ? '12px 0' : '16px')};
    cursor: ${(props) => (props.isPseudoSelect ? 'pointer' : null)};
    z-index: 1;

    ${media.desktop1920} {
        padding: ${(props) => (props.isLinedInput ? '0 0 12px 0' : '20px 24px')};
    }

    input,
    input[type='search'],
    textarea {
        font-family: ${(props) => (props.isSmsInput ? 'Courier New,monospace' : 'var(--font-main), sans-serif')};
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        background-color: transparent;
        color: ${(props) => getInputColor(props)};
        border: none;
        box-sizing: border-box;
        display: inline-block;
        padding: ${(props) => (props.isLinedInput ? '24px 0 0 0' : props.isPseudoSelect ? '0 24px 0 0' : '0')};
        width: 100%;
        font-size: ${(props) => (props.isCalculatorInput ? '40px' : '17px')};
        line-height: ${(props) => (props.isCalculatorInput ? '48px' : '24px')};
        text-overflow: ${(props) => (props.isCalculatorInput ? null : 'ellipsis')};
        cursor: ${(props) => (props.isPseudoSelect ? 'pointer' : 'auto')};
        text-align: ${(props) => (props.isSmsInput || props.isCenteredInput || props.isCalculatorInput ? 'center' : null)};
        font-weight: ${(props) => (props.isSmsInput ? '700' : null)};
        letter-spacing: ${(props) => (props.isSmsInput ? '16px' : null)};
        outline: none;

        /* TODO: legacy calculator hover was purple — confirm --text_primary_main_hovered. */
        &:hover {
            color: ${(props) => getInputHoverColor(props)};
        }

        &:-webkit-autofill {
            -webkit-box-shadow: inset 0 0 0 50px var(--bg_primary_main_default);
            -webkit-text-fill-color: var(--text_primary_main_default);
            color: var(--text_primary_main_default) !important;
        }

        ${media.desktop1920} {
            padding: ${(props) => (props.isLinedInput ? '28px 0 0 0' : props.isPseudoSelect ? '0 28px 0 0' : '0')};
            font-size: ${(props) => (props.isCalculatorInput ? '48px' : '20px')};
            line-height: ${(props) => (props.isCalculatorInput ? '64px' : '32px')};
            letter-spacing: ${(props) => (props.isSmsInput ? '18px' : null)};
        }
    }

    /* custom phone library styling */
    .react-tel-input {
        width: 100%;

        .flag-dropdown {
            display: none;
        }
    }
`;

export const FloatingLabel = styled.span.withConfig({ shouldForwardProp: shouldForwardFloatingLabelProp })`
    position: absolute;
    top: ${(props) => getFloatingLabelPosition(props)};
    left: ${(props) => getFloatingLabelLeftPosition(props)};
    font-size: ${(props) => (props.isFilled || props.isFocused ? '14px' : '17px')};
    line-height: ${(props) => (props.isFilled || props.isFocused ? '20px' : '24px')};
    color: ${(props) => getLabelColor(props)};
    transition: top 0.2s;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: calc(100% - 16px);
    box-sizing: border-box;
    padding: ${(props) => (props.isLinedInput ? null : '0 8px')};
    background: ${(props) => (props.isLinedInput ? null : 'var(--bg_primary_main_default)')};
    width: ${(props) => (props.isCenteredInput ? 'calc(100% - 32px)' : null)};
    text-align: ${(props) => (props.isCenteredInput ? 'center' : null)};
    display: ${(props) => (props.isCenteredInput && (props.isFocused || props.isFilled) ? 'none' : null)};

    ${media.desktop1920} {
        top: ${(props) => getFloatingLabelDesktopPosition(props)};
        left: ${(props) => getFloatingLabelLeftDesktopPosition(props)};
        font-size: ${(props) => (props.isFilled || props.isFocused ? '17px' : '20px')};
        line-height: ${(props) => (props.isFilled || props.isFocused ? '24px' : '32px')};
    }
`;

export const DiskButtonContainer = styled.span`
    margin-left: 4px;
`;

export const HintContainer = styled.div.withConfig({ shouldForwardProp: shouldForwardHintContainerProp })`
    margin-top: 8px;
    padding: ${(props) => (props.isLinedInput ? null : '0 16px')};
    text-align: left;
    display: flex;
    min-height: ${(props) => (props.hasComment ? '56px' : props.alwaysHasHint ? '24px' : null)};
    margin-bottom: ${(props) => (props.alwaysHasHint ? '8px' : null)};

    ${media.desktop1920} {
        padding: ${(props) => (props.isLinedInput ? null : '0 24px')};
    }
`;

export const HintButton = styled.span`
    margin-top: -4px;
    margin-left: 2px;
`;

export const InputRightElement = styled.div.withConfig({ shouldForwardProp: shouldForwardInputRightElementProp })`
    position: absolute;
    right: ${(props) => (props.isInfoButtonForPseudoselect ? '46px' : '16px')};
    top: ${(props) => (props.isInfoButton ? '14px' : '16px')};
    z-index: ${(props) => (props.isInfoButton ? '1' : null)};

    ${media.desktop1920} {
        right: ${(props) => (props.isInfoButtonForPseudoselect ? '54px' : '16px')};
        top: ${(props) => (props.isInfoButton ? '18px' : '24px')};
    }
`;
